import { useEffect, useMemo } from 'react';
import { usePolling } from './usePolling';
import { liveService } from '../services/liveService';
import {
  useLocationStatusStore,
  LocationRealtimeStatus,
  LocationDisplayStatus,
  deriveDisplayStatus,
} from '../store/locationStatusStore';
import { ChargingLocation } from '../types';
import type { LocationStatusResponse } from '../types/live';

interface UseMapLocationStatusOptions {
  enabled?: boolean;
}

interface UseMapLocationStatusReturn {
  isConnected: boolean;
  getLocationStatus: (locationId: string) => LocationRealtimeStatus | undefined;
  getDisplayStatus: (locationId: string) => LocationDisplayStatus | undefined;
  getEffectiveAvailability: (location: ChargingLocation) => {
    isAvailable: boolean;
    displayStatus: LocationDisplayStatus;
    availableCount: number;
    totalCount: number;
  };
}

export function useMapLocationStatus(
  options: UseMapLocationStatusOptions = {}
): UseMapLocationStatusReturn {
  const { enabled = true } = options;

  const { data } = usePolling<LocationStatusResponse>({
    fetcher: () => liveService.getLocationStatuses(),
    interval: 30_000,
    enabled,
  });

  const updateLocationStatus = useLocationStatusStore((s) => s.updateLocationStatus);
  useEffect(() => {
    if (!data?.locations) return;
    for (const loc of data.locations) {
      updateLocationStatus(loc);
    }
  }, [data, updateLocationStatus]);

  const statuses = useLocationStatusStore((state) => state.statuses);
  const getLocationStatus = useLocationStatusStore((state) => state.getLocationStatus);
  const getDisplayStatus = useLocationStatusStore((state) => state.getDisplayStatus);

  const getEffectiveAvailability = useMemo(() => {
    return (location: ChargingLocation) => {
      const realtimeStatus = statuses.get(location.id);

      if (realtimeStatus) {
        return {
          isAvailable: realtimeStatus.isAvailable,
          displayStatus: deriveDisplayStatus(realtimeStatus),
          availableCount: realtimeStatus.availableEvses,
          totalCount: realtimeStatus.totalEvses,
        };
      }

      const onlineChargePoints = location.chargepoints.filter(cp => cp.isOnline);
      const onlineCount = onlineChargePoints.length;
      const availableCount = onlineChargePoints.filter(
        (cp) => cp.status === 'available'
      ).length;

      const displayStatus: LocationDisplayStatus =
        onlineCount === 0
          ? 'offline'
          : (availableCount > 0 ? 'available' : 'occupied');

      return {
        isAvailable: onlineCount > 0 && availableCount > 0,
        displayStatus,
        availableCount,
        totalCount: location.chargepoints.length,
      };
    };
  }, [statuses]);

  return {
    isConnected: !!data,
    getLocationStatus,
    getDisplayStatus,
    getEffectiveAvailability,
  };
}
